import { Database, LanguageLevel } from '../types/database'

type Card = Database['public']['Tables']['cards']['Row']

export interface LocalProgress {
  mastery_score: number
  status: 'active' | 'mastered' | 'hidden'
  last_reviewed: string | null
  review_count: number
}

export interface LevelSettings {
  enabledLevels: LanguageLevel[]
  filterMode: 'only' | 'prefer'
}

/**
 * Get priority value for a CEFR level (higher levels = higher priority)
 */
function getLevelPriority(level: LanguageLevel | undefined): number {
  if (!level) return -1
  const levels: LanguageLevel[] = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2']
  return levels.indexOf(level)
}

/**
 * Sort cards for study rotation based on level preferences and mastery score.
 * Cards are sorted so that:
 * 1. In 'prefer' mode: Selected levels come first, then by level priority
 * 2. In 'only' mode: Higher levels first
 * 3. Then by mastery score (higher first)
 * 4. Then by last review time (older reviews first)
 */
export function sortCardsForStudy(
  cards: Card[],
  progressMap: Map<string, LocalProgress>,
  levelSettings: LevelSettings
): Card[] {
  return [...cards].sort((a, b) => {
    const aProgress = progressMap.get(a.id)
    const bProgress = progressMap.get(b.id)

    const aScore = aProgress?.mastery_score || 0
    const bScore = bProgress?.mastery_score || 0

    // First, sort by level preference
    const aLevelPriority = getLevelPriority(a.level)
    const bLevelPriority = getLevelPriority(b.level)

    if (levelSettings.filterMode === 'prefer') {
      // In prefer mode, prioritize selected levels first
      const aIsSelected = a.level && levelSettings.enabledLevels.includes(a.level)
      const bIsSelected = b.level && levelSettings.enabledLevels.includes(b.level)

      if (aIsSelected && !bIsSelected) return -1
      if (!aIsSelected && bIsSelected) return 1

      // Within selected or unselected groups, sort by level (highest first)
      if (aIsSelected && bIsSelected && aLevelPriority !== bLevelPriority) {
        return bLevelPriority - aLevelPriority // Higher levels first
      }
    } else {
      // In only mode, sort by level (highest first)
      if (aLevelPriority !== bLevelPriority) {
        return bLevelPriority - aLevelPriority // Higher levels first
      }
    }

    // Then sort by mastery score (higher score first)
    if (aScore !== bScore) {
      return bScore - aScore
    }

    // If tied, prioritize cards that haven't been reviewed recently
    const aLastReview = aProgress?.last_reviewed ? new Date(aProgress.last_reviewed).getTime() : 0
    const bLastReview = bProgress?.last_reviewed ? new Date(bProgress.last_reviewed).getTime() : 0

    return aLastReview - bLastReview // Older reviews first
  })
}

/**
 * Filter cards based on selected exercises, tags, and level settings.
 * selectedExercises contains exercise IDs (UUIDs), matched against card.exercise_id.
 */
export function filterCardsForStudy(
  cards: Card[],
  selectedExercises: string[],
  selectedTags: string[],
  levelSettings: LevelSettings
): Card[] {
  return cards.filter(card => {
    // If both exercises and tags are selected, card must match at least one
    const hasExerciseFilter = selectedExercises.length > 0
    const hasTagFilter = selectedTags.length > 0

    if (hasExerciseFilter || hasTagFilter) {
      const matchesExercise = hasExerciseFilter && card.exercise_id != null && selectedExercises.includes(card.exercise_id)
      const matchesTag = hasTagFilter && selectedTags.some(tag => card.tags.includes(tag))

      // OR logic: card matches if it matches any selected exercise OR any selected tag
      if (!matchesExercise && !matchesTag) {
        return false
      }
    }

    // Level filter (only mode)
    if (levelSettings.filterMode === 'only' && card.level) {
      if (!levelSettings.enabledLevels.includes(card.level)) {
        return false
      }
    }

    return true
  })
}
